/**
 * The services the ERP depends on, as Settings → Dependency Sync shows them.
 *
 * One entry per robot, live or planned. The live state comes from
 * GET /api/sumal1n/status (sources.<key>); what each robot is, does and never
 * does is written here, once, in plain English: the people who read this page
 * at 7 in the morning are not developers.
 *
 * `key` is the ERP key of the robot (sumal_sync_run.source,
 * sumal_sync_setting.source, /api/sumal1n/settings/:key). SumalX2n keeps the
 * key "sumalx2": the robot was called SumalX2 until 2026-10-18 and the key is
 * stored in the database. Only the name changed.
 *
 * A planned service (live: false) has its tile and its description and gets
 * a status the day its robot writes runs. SagaX1n and SagaX2n (live since
 * 2026-10-21) replace SagaX1 and SagaX2, the C# robots of the SAGA computer;
 * their "Test" and "Documents" tabs are parts/SagaPanel.vue.
 */
import { ago, hoursSince, roTime, roWhen } from '@/helpers/romaniaTime'

export const FAMILIES = [
  {
    key: 'sumal',
    name: 'SUMAL',
    what: 'The Romanian wood traceability system. These robots copy it into the ERP.'
  },
  {
    key: 'saga',
    name: 'SAGA',
    what: 'The accounting software (SAGA Web). These robots copy the ERP into it, once a day.'
  }
]

const esc = (v) => String(v === null || v === undefined ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const SERVICES = [
  {
    key: 'sumalx1n',
    family: 'sumal',
    name: 'SumalX1n',
    topic: 'Avize',
    from: 'SUMAL',
    to: 'ERP',
    live: true,
    what: 'Each harvest truck becomes a production. Each truck from another company arriving at the yard becomes a reception.',
    host: 'VPS',
    dir: '/home/ubuntu/sumalx1n',
    cmd: 'node bin/sumalx1n.js',
    log: '/home/ubuntu/logs/sumalx1n.log',
    replaces: 'SumalX1, the C# robot of the Windows PC, stopped on 6 October 2026',
    how: (src) => [
      { k: 'What it does', html: 'It reads SUMAL at the hours below. Each truck leaving a harvest site becomes a <b>Production</b> in the ERP. Each truck from another company arriving at the yard (TEIU) becomes a <b>Reception</b>, with its supplier. The supplier is found by its fiscal code, then by its name. If it does not exist, it is created.' },
      { k: 'What it never does', html: 'It never deletes anything. If SUMAL says an aviz is cancelled, rejected or seized, it is not imported. If that happens after the import, the aviz goes to "To look at" for someone to check. An aviz already in the ERP is left alone.' },
      { k: 'Where and when', html: `On the VPS, at ${esc(src.schedule)}. The VPS asks the ERP every 5 minutes if one of these hours has passed. Each run reads the last ${esc(src.settings.lookback_days)} days. Log: <code>/home/ubuntu/logs/sumalx1n.log</code>.` },
      { k: 'By hand, on the VPS', html: '<code>cd /home/ubuntu/sumalx1n &amp;&amp; node bin/sumalx1n.js sync --dry-run</code> shows what it would do, without writing. <code>node bin/sumalx1n.js sync --force</code> runs now. <code>node bin/sumalx1n.js sync --days 60</code> reads 60 days back, once.' },
      { k: 'If something is wrong', html: '"Failing" shows the step. SUMAL login refused: check the SUMAL account. ERP error: check the API. A missed run with no run at all: the cron is not running. "Needs a look": the run worked, but some avize could not be imported. They are listed under "To look at" and retried at every run.' },
      { k: 'Mail', html: 'After each run, or only when something is new or wrong, or never (see Settings). It lists every operation created, with a link, and everything to look at.' },
      { k: 'Before', html: 'It replaces SumalX1, the robot of the Windows PC, stopped on 6 October 2026. SumalX1 lost the FSC avize and the avize of the last days of a month, ignored the SUMAL status, and kept no record of what it did.' }
    ]
  },
  {
    key: 'sumalx2',
    family: 'sumal',
    name: 'SumalX2n',
    topic: 'Depots',
    from: 'SUMAL',
    to: 'ERP',
    live: true,
    what: 'Every SUMAL depot with its permit dates and status, its stock, the trucks entering the yard, and the volume corrections of each APV.',
    host: 'VPS',
    dir: '/home/ubuntu/sumalx2n',
    cmd: 'node bin/sumalx2n.js',
    log: '/home/ubuntu/logs/sumalx2n.log',
    how: (src) => [
      { k: 'What it does', html: 'It copies the SUMAL depot list into the ERP: every depot with its permit dates and status, the stock of each APV depot, the trucks entering the yard (TEIU), and the volume corrections SUMAL holds on each APV (handover write-offs, lost wood, APV or stock corrections). Harvesting 2.0, the "new APV" list and the morning report read from it.' },
      { k: 'What it never does', html: 'It writes nothing back to SUMAL. On a parcel it only fills the SUMAL dates and status: prices, volumes and decisions stay human. If SUMAL returns almost nothing, the run is refused instead of erasing the dates.' },
      { k: 'Where and when', html: `On the VPS, at ${esc(src.schedule)}. The VPS asks the ERP every 5 minutes if one of these hours has passed. The first run must stay before the 07:00 morning report, and before SumalX1n. Log: <code>/home/ubuntu/logs/sumalx2n.log</code>.` },
      { k: 'By hand, on the VPS', html: '<code>cd /home/ubuntu/sumalx2n &amp;&amp; node bin/sumalx2n.js sync --dry-run</code> shows what it would do. <code>node bin/sumalx2n.js sync --force</code> runs now.' },
      { k: 'If something is wrong', html: `"Failing" shows the step. "Late" means no good run for ${esc(src.late_hours)} h, so two runs went by: the permit dates in Harvesting 2.0 are old. A missed run with no run at all: the cron is not running.` },
      { k: 'Mail', html: 'Same as SumalX1n. "Only when something is new or wrong" means: a new depot, a new parcel, a new volume correction, a warning or a failure.' },
      { k: 'Name', html: 'SumalX2n = SumalX2 in Node, like SumalX1n. It was called SumalX2 until 18 October 2026.' }
    ]
  },
  {
    key: 'sagax1n',
    family: 'saga',
    name: 'SagaX1n',
    topic: 'Sales invoices',
    from: 'ERP',
    to: 'SAGA',
    live: true,
    what: 'Each sales invoice of the ERP is imported into SAGA, the night after its date. Not validated: the accountant validates.',
    host: 'VPS',
    dir: '/home/ubuntu/sagax1n',
    cmd: 'node bin/sagax1n.js',
    log: '/home/ubuntu/logs/sagax1n.log',
    replaces: 'SagaX1, the C# robot of the SAGA computer (XML files imported by hand twice a month)',
    how: (src) => [
      { k: 'What it does', html: `At ${esc(src.schedule)}, it takes the invoices of the ERP dated before today (last ${esc(src.settings.lookback_days)} days) that are not in SAGA yet, and imports each one through the SAGA <b>Import date</b> screen: the same XML file SagaX1 made. SAGA files it in <b>Ieșiri</b> (lei) or <b>Ieșiri – valută</b> (EUR). The customer is found by its fiscal code, or created by SAGA.` },
      { k: 'What it never does', html: 'It never <b>validates</b>: the accountant does. It never imports an invoice SAGA already has (same number): it marks it as sent. Same number on another date: it stops and says so. It never deletes anything in SAGA.' },
      { k: 'After it is sent', html: 'The invoice can no longer change what SAGA holds of it: customer, date, number, billing address, VAT, lines, cancelling. Status, dispute, notes, mails and payments stay free. To change it: delete it in SAGA, then <b>Release</b> it in the Test tab.' },
      { k: 'Not sent', html: 'An invoice with no billing address, cancelled, or with no line is not sent: the reason is in Documents → To look at, and it is tried again every night. The old robot sent an invoice without address with no country.' },
      { k: 'Customers confirmed by hand', html: 'For a few customers (Kronospan Timber, Kronospan Trading, DML Invest: table <b>saga_hold_customer</b>), the run never takes an invoice on its own. Someone checks it, then clicks <b>Ready to be sent to SAGA</b> at the bottom of the invoice; the next night it goes. Until then the invoice list shows <b>Waiting confirmation</b>. A Send from the Test tab counts as that click; a Release puts the invoice back to waiting.' },
      { k: 'Test on one invoice', html: 'Tab <b>Test</b>: find the invoice, see what would be imported, then <b>Check in SAGA</b> (nothing written) or <b>Send to SAGA</b>. The robot looks every minute. A test works even when the robot is paused.' },
      { k: 'Where and when', html: `On the VPS. Every minute it asks the ERP if there is something to do: the night run (${esc(src.schedule)}, Romania time) or a test. Log: <code>/home/ubuntu/logs/sagax1n.log</code>.` },
      { k: 'By hand, on the VPS', html: '<code>cd /home/ubuntu/sagax1n &amp;&amp; node bin/sagax1n.js check &lt;id&gt;</code> checks one invoice (nothing written). <code>node bin/sagax1n.js send &lt;id&gt;</code> imports it now. <code>node bin/sagax1n.js sync --dry-run --force</code> shows what the night run would do.' },
      { k: 'If something is wrong', html: '"SAGA does not know this computer": SAGA mailed an authorisation link to the SAGA account, click it. The robot does not log in again for 12 hours (each attempt sends one more mail); a test does. Login refused: check the SAGA account in the robot\'s .env.' }
    ]
  },
  {
    key: 'sagax2n',
    family: 'saga',
    name: 'SagaX2n',
    topic: 'Productions',
    from: 'ERP',
    to: 'SAGA',
    live: true,
    what: 'Each production of the ERP is entered in the SAGA stock, the night after its date: the wood used and the products made. Not validated: the accountant validates.',
    host: 'VPS',
    dir: '/home/ubuntu/sagax2n',
    cmd: 'node bin/sagax2n.js',
    log: '/home/ubuntu/logs/sagax2n.log',
    replaces: 'SagaX2, the C# robot that typed into SAGA with the keyboard',
    how: (src) => [
      { k: 'What it does', html: `At ${esc(src.schedule)}, it takes the productions of the ERP dated before today (last ${esc(src.settings.lookback_days)} days) not in SAGA yet, and types them into the SAGA <b>Producție</b> screen: one bon per product (where it enters: raw wood in MATERII PRIME TEIU, the rest in PRODUCTIE TEIU) with what it consumed below. Reference "PR" + the production number in "Informații suplimentare", as before.` },
      { k: 'The rules', html: 'The rules of SagaX2, unchanged: the SAGA code of each category, bark, sawdust and wood waste at 100 without consumption, cleft paling at 1000, every other price set by SAGA from the consumption when the accountant validates.' },
      { k: 'What it never does', html: 'It never <b>validates</b>, never deletes, and never types a production twice: it reads the bons SAGA already has (same reference, same year). Half there: it stops and says so.' },
      { k: 'Not sent', html: 'A production with a product without volume, with nothing consumed, or with a SAGA question the robot does not know (e.g. not enough stock) is not sent: the reason is in Documents → To look at, and it is tried again every night.' },
      { k: 'Test on one production', html: 'Tab <b>Test</b>: find the production, see the bons, then <b>Check in SAGA</b> (SAGA checks the bons, nothing written) or <b>Send to SAGA</b>. A test works even when the robot is paused.' },
      { k: 'Where and when', html: `On the VPS, every minute: the night run (${esc(src.schedule)}, Romania time) or a test. Log: <code>/home/ubuntu/logs/sagax2n.log</code>. It shares the SAGA account and its authorisation with SagaX1n; the two take turns.` },
      { k: 'By hand, on the VPS', html: '<code>cd /home/ubuntu/sagax2n &amp;&amp; node bin/sagax2n.js show &lt;id&gt;</code> shows the bons. <code>check &lt;id&gt;</code> lets SAGA check them. <code>send &lt;id&gt;</code> types them in now.' },
      { k: 'Before', html: 'SagaX2 typed with the keyboard into SAGA desktop: it froze on a product without volume ("NaN"), and its "M + arrow down" put about 285 bons of July–October 2026 in MARFURI instead of MATERII PRIME.' }
    ]
  }
]

export const serviceByKey = (key) => SERVICES.find((s) => s.key === key) || null

// A run, or a planned hour, in words: [tone, label]. "running" on a planned
// hour = its 45 minutes of grace are not over yet.
export const RUN = {
  ok: ['ok', 'Good'],
  partial: ['warn', 'Incomplete'],
  failed: ['danger', 'Failed'],
  missed: ['danger', 'Missed'],
  running: ['info', 'Waiting'],
  // Switched off in its settings at that hour: not a missed run.
  paused: ['', 'Paused']
}
export const runTone = (st) => (RUN[st] || ['', st])[0]
export const runLabel = (st) => (RUN[st] || ['', st || '—'])[1]

/** "12 of 12 good" / "10 of 12 good · 1 failed · 1 missed" over planned hours. */
export function slotsSummary (slots) {
  const c = { ok: 0, partial: 0, failed: 0, missed: 0, running: 0, paused: 0 }
  for (const s of slots || []) c[s.status] = (c[s.status] || 0) + 1
  const total = c.ok + c.partial + c.failed + c.missed
  if (!total) {
    if (c.paused) return { text: 'Paused: no planned run', bad: false }
    return { text: c.running ? 'First planned run not over yet' : 'No planned run in 3 days', bad: false }
  }
  const parts = [`${c.ok} of ${total} good`]
  if (c.partial) parts.push(`${c.partial} incomplete`)
  if (c.failed) parts.push(`${c.failed} failed`)
  if (c.missed) parts.push(`${c.missed} missed`)
  if (c.paused) parts.push(`${c.paused} paused`)
  return { text: parts.join(' · '), bad: c.failed + c.missed > 0 }
}

/**
 * The state of a service in words: a label, one sentence, and what to do.
 * `rank` orders the problems (higher = worse); the page banner and the tiles
 * read the same object, so they can never disagree.
 */
export function healthOf (service, src, now = new Date()) {
  if (!service.live) {
    return { tone: '', icon: 'ClockIcon', label: 'Coming soon', line: 'Not active yet.', advice: service.today, rank: 0 }
  }
  if (!src) {
    return { tone: '', icon: 'HelpCircleIcon', label: 'Unknown', line: 'The status could not be read.', advice: '', rank: 1 }
  }
  const run = src.last_run
  const sinceGood = src.hours_since_success
  if (src.state === 'paused') {
    return { tone: 'info', icon: 'PauseCircleIcon', label: 'Paused', line: 'Switched off in its settings. It does nothing until it is switched on again.', advice: 'Switch it on in Settings.', rank: 2 }
  }
  if (run && run.status === 'running') {
    const h = hoursSince(run.started_at, now)
    if (h !== null && h < 0.5) {
      return { tone: 'info', icon: 'LoaderIcon', label: 'Running now', line: `Started at ${roTime(run.started_at)}. Refresh in a minute to see the result.`, advice: '', rank: 1 }
    }
    return { tone: 'warn', icon: 'AlertTriangleIcon', label: 'Stuck', line: `The run of ${roWhen(run.started_at, now)} started and never finished.`, advice: `The robot may have crashed. Look at its log on the VPS: ${service.log}.`, rank: 3 }
  }
  if (src.state === 'never') {
    return { tone: 'danger', icon: 'AlertCircleIcon', label: 'Never ran', line: 'No run recorded yet.', advice: 'The cron is probably not installed. From the robot folder: ./deploy.sh cron.', rank: 4 }
  }
  if (src.state === 'failed') {
    const where = run && run.stage ? ` at step "${run.stage}"` : ''
    return { tone: 'danger', icon: 'XCircleIcon', label: 'Failing', line: `The last run failed${where}, ${ago(hoursSince(run && run.started_at, now))} ago.`, advice: service.family === 'saga'
      ? 'Read the error below. SAGA login refused or "does not know this computer": see How it works. ERP error: check the API. The robot tries again in 30 minutes.'
      : 'Read the error below. SUMAL login refused: check the SUMAL account. ERP error: check the API. The robot tries again at its next hour.', rank: 5 }
  }
  if (src.state === 'late') {
    return { tone: 'danger', icon: 'ClockIcon', label: 'Late', line: src.last_success ? `No good run for ${ago(sinceGood)}. The limit is ${src.late_hours} h.` : 'No good run yet.', advice: 'The data in the ERP is getting old. Look at the last runs below and at the log on the VPS.', rank: 4 }
  }
  if (src.state === 'partial') {
    return { tone: 'warn', icon: 'AlertTriangleIcon', label: 'Needs a look', line: 'The last run worked, but not completely.', advice: 'The warning is below. What could not be handled is tried again at every run.', rank: 3 }
  }
  if (src.missed_slots) {
    return { tone: 'warn', icon: 'AlertTriangleIcon', label: 'Working, run missed', line: `${src.missed_slots} planned run${src.missed_slots > 1 ? 's' : ''} did not start in 3 days. Last good run ${ago(sinceGood)} ago.`, advice: 'A planned hour with no run at all means the VPS did not start the robot. If it happens again, check the cron.', rank: 2 }
  }
  return { tone: 'ok', icon: 'CheckCircleIcon', label: 'Working', line: `Last good run ${ago(sinceGood)} ago. Next run ${roWhen(src.next_slot, now)}.`, advice: '', rank: 1 }
}
